/**
 * Upload reports: what arrived at the fleet, per Baghdad day, per center.
 *
 * Counted from each center's own Orthanc index by the rollup job, not from
 * health samples — so the history goes back as far as each index does, and a
 * center moved into the panel later still gets all of it once backfilled.
 *
 * What the words on this page mean (the same definitions the backend's
 * module docstrings carry, repeated in the UI where a reader needs them):
 *
 * * **Studies** — new studies, each counted once, on the day its FIRST
 *   instance arrived. Additive across days and centers.
 * * **Images** — frames of pixel-bearing instances (a 300-frame cine is 300
 *   images; an SR or a presentation state is none). Instances an old Orthanc
 *   could not classify are assumed to be images and counted as "legacy" so
 *   the headline can say so.
 * * **Instances** — every DICOM object received, images or not.
 * * **Data** — bytes received, as stored.
 *
 * Four things this page is careful about, each a way an upload chart lies:
 *
 * * **Not computed is not zero.** A center still being backfilled, or whose
 *   database the report role cannot read yet, is null in every number and
 *   named in a callout — never folded into the sum as a quiet 0.
 * * **Today is still counting.** The current bucket is marked partial (and
 *   faded in the chart) rather than read as a sudden drop.
 * * **Edge counts are never added here.** Studies that arrive at a hospital
 *   edge node are already inside the center's index the day they replicate;
 *   the edge's own counts are a separate view of the same studies.
 * * **The filters live in the URL,** so a drill-down (`?slug=`) or a window
 *   can be pasted to a colleague and opens on the same numbers.
 */

import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge } from '../components/Badge';
import type { Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { ReportTabs } from '../components/ReportTabs';
import { EdgeUploadsSection } from '../components/reports/EdgeUploadsSection';
import { SortHeader, TableScroll, useSort, useSortedRows } from '../components/SortableTable';
import { StatGrid, StatTile } from '../components/StatTile';
import { ErrorBlock, LoadingBlock } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Select, SelectItem, TextInput } from '../components/ui/Form';
import { DownloadIcon, RenewIcon } from '../components/ui/Icon';
import {
  ChartFrame,
  ColumnChart,
  ColumnChartTable,
  Heatmap,
  HeatmapTable,
  LineChart,
  RankedBars,
  WEEKDAYS,
} from '../components/viz/Chart';
import type { Series } from '../components/viz/Chart';
import { apiPost, describeError } from '../lib/api';
import { baghdadToday, daysInclusive, isDay, presetRange } from '../lib/baghdad';
import type { RangePreset } from '../lib/baghdad';
import { downloadCsv } from '../lib/csv';
import type { CsvCell } from '../lib/csv';
import {
  formatBytes,
  formatDateTime,
  formatNumber,
  formatRelative,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  CenterListResponse,
  IdleCenterRow,
  IdleCentersResponse,
  MeResponse,
  RollupRefreshResponse,
  RollupStatusResponse,
  UploadBucket,
  UploadGroupBy,
  UploadHeatmapCell,
  UploadHeatmapMeasure,
  UploadHeatmapResponse,
  UploadMeasure,
  UploadPoint,
  UploadSeriesResponse,
  UploadStorageResponse,
  UploadTotals,
} from '../types/api';

// ── filters ──────────────────────────────────────────────────────────────────

type PresetKey = RangePreset | 'custom';

/** Windows people ask for, each with the bucket that suits it. */
const PRESETS: readonly { key: PresetKey; label: string; bucket: UploadBucket }[] = [
  { key: 'today', label: 'Today', bucket: 'day' },
  { key: '7d', label: 'Last 7 days', bucket: 'day' },
  { key: '30d', label: 'Last 30 days', bucket: 'day' },
  { key: '90d', label: 'Last 90 days', bucket: 'week' },
  { key: '12m', label: 'Last 12 months', bucket: 'month' },
  { key: 'custom', label: 'Custom range…', bucket: 'day' },
];
const DEFAULT_PRESET: PresetKey = '30d';

const BUCKETS: readonly { key: UploadBucket; label: string }[] = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly (from Monday)' },
  { key: 'month', label: 'Monthly' },
];

const GROUPINGS: readonly { key: UploadGroupBy; label: string }[] = [
  { key: 'none', label: 'Nothing (one total)' },
  { key: 'center', label: 'Center' },
  { key: 'modality', label: 'Modality' },
  { key: 'source', label: 'Sender' },
];

const MEASURES: readonly { key: UploadMeasure; label: string; noun: string }[] = [
  { key: 'studies', label: 'Studies', noun: 'studies' },
  { key: 'frames', label: 'Images (frames)', noun: 'images' },
  { key: 'instances', label: 'Instances', noun: 'instances' },
  { key: 'bytes', label: 'Data', noun: 'data' },
];

/** The server refuses longer windows; saying so here beats a 400. */
const MAX_WINDOW_DAYS = 400;
/** Days without an upload before a center is called idle. */
const IDLE_THRESHOLD_DAYS = 7;
/** Groups that keep their own colour; the rest fold into "Other" (slot 8),
 *  the chart palette's rule — a ninth hue would be a guess, not an identity. */
const TOP_GROUPS = 7;

type UrlKey = 'range' | 'from' | 'to' | 'bucket' | 'slug' | 'group_by' | 'measure';

function readPreset(value: string | null): PresetKey {
  return PRESETS.some((p) => p.key === value) ? (value as PresetKey) : DEFAULT_PRESET;
}
function readBucket(value: string | null): UploadBucket | null {
  return BUCKETS.some((b) => b.key === value) ? (value as UploadBucket) : null;
}
function readGroupBy(value: string | null): UploadGroupBy {
  return GROUPINGS.some((g) => g.key === value) ? (value as UploadGroupBy) : 'none';
}
function readMeasure(value: string | null): UploadMeasure {
  return MEASURES.some((m) => m.key === value) ? (value as UploadMeasure) : 'studies';
}

/**
 * A custom range's natural bucket. Past about two months daily columns are
 * too thin to read, and past half a year weekly ones are — the same steps the
 * presets take.
 */
function bucketForSpan(days: number): UploadBucket {
  if (days <= 62) return 'day';
  if (days <= 190) return 'week';
  return 'month';
}

// ── formatting ───────────────────────────────────────────────────────────────

function formatMeasure(measure: UploadMeasure) {
  return measure === 'bytes'
    ? (value: number) => formatBytes(value)
    : (value: number) => formatNumber(Math.round(value));
}

type CountRow = Pick<UploadTotals, 'studies_new' | 'frames' | 'instances' | 'bytes'>;

/** The UI measure (kept as `studies` in the URL) → the field the API sends. */
const MEASURE_FIELD: Record<UploadMeasure, keyof CountRow> = {
  studies: 'studies_new',
  frames: 'frames',
  instances: 'instances',
  bytes: 'bytes',
};

function measureOf(row: CountRow, measure: UploadMeasure): number | null {
  const value = row[MEASURE_FIELD[measure]];
  return value === null || value === undefined || !Number.isFinite(value) ? null : value;
}

/** Every center-day of the bucket was computed. */
function isComplete(p: UploadPoint): boolean {
  return p.days_computed >= p.days_expected;
}

/** Buckets are calendar days (`YYYY-MM-DD`), formatted in UTC where
 *  `Date.parse` puts them, so the browser's zone can never move them a day. */
function dayLabel(day: string, options: Intl.DateTimeFormatOptions): string {
  const at = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(at)
    ? new Date(at).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' })
    : day;
}

function bucketLabeller(bucket: UploadBucket): (at: string) => string {
  switch (bucket) {
    case 'day':
      return (at) => dayLabel(at, { month: 'short', day: 'numeric' });
    case 'week':
      return (at) => `Wk ${dayLabel(at, { month: 'short', day: 'numeric' })}`;
    case 'month':
      return (at) => dayLabel(at, { month: 'short', year: 'numeric' });
  }
}

function rangeText(from: string, to: string): string {
  return from === to ? `${from} (Baghdad day)` : `${from} to ${to} (Baghdad days)`;
}

function sharePercent(value: number | null | undefined): string {
  return value === null || value === undefined ? UNKNOWN : `${(value * 100).toFixed(1)}%`;
}

/**
 * A LineChart x value for a Baghdad day.
 *
 * LineChart takes instants and labels them in the browser's zone. Midday UTC
 * is the same calendar date in every zone from UTC−11 to UTC+11, so a day
 * plotted there is labelled as itself wherever the reader is.
 */
function dayAsInstant(day: string): string {
  return `${day}T12:00:00Z`;
}

/** Status of a center's rollup, as a badge: why a number is missing. */
const PENDING_TONE: Record<string, Tone> = {
  grant: 'critical',
  unreachable: 'critical',
  timeout: 'serious',
  error: 'critical',
  stale: 'warning',
  pending: 'neutral',
  backfilling: 'info',
};

/** A center the rollup has not fully counted, and why. */
interface PendingCenter {
  slug: string;
  status: string;
  reason: string;
  history_from: string | null;
}

function idleBadge(row: IdleCenterRow): { tone: Tone; text: string } {
  switch (row.status) {
    case 'idle': {
      // The server reports a center that never uploaded as idle with no
      // last upload; that is a different fact from a center gone quiet.
      if (row.last_upload_at === null) return { tone: 'neutral', text: 'Never uploaded' };
      const days = row.days_since_upload;
      return {
        tone: days !== null && days >= 30 ? 'critical' : 'warning',
        text: days === null ? 'Idle' : `Idle ${formatNumber(days)} ${pluralise(days, 'day')}`,
      };
    }
    case 'active':
      return { tone: 'good', text: 'Active' };
    default:
      // A stale or failing rollup is not evidence of a quiet center.
      return { tone: 'neutral', text: 'Unknown' };
  }
}

// ── the chart's series ───────────────────────────────────────────────────────

/**
 * Points → one Series per group, the biggest `TOP_GROUPS` by the chosen
 * measure keeping fixed slots and the rest summed into "Other".
 *
 * "Other" is null in a bucket where every folded group is null — the fold
 * must not turn "none of these were counted" into 0.
 */
function buildSeries(
  data: UploadSeriesResponse | null,
  groups: GroupSummary[],
  measure: UploadMeasure,
  measureLabel: string,
): Series[] {
  if (!data) return [];
  if (data.group_by === 'none') {
    return [
      {
        key: 'total',
        label: measureLabel,
        slot: 1,
        points: data.points.map((p) => ({ at: p.bucket, value: measureOf(p, measure) })),
      },
    ];
  }

  const ranked = [...groups].sort(
    (a, b) => (measureOf(b, measure) ?? -1) - (measureOf(a, measure) ?? -1),
  );
  const kept = ranked.slice(0, ranked.length > TOP_GROUPS + 1 ? TOP_GROUPS : TOP_GROUPS + 1);
  const keptKeys = new Set(kept.map((g) => g.key));
  const folded = ranked.length - kept.length;

  const byGroup = new Map<string, UploadPoint[]>();
  for (const point of data.points) {
    const key = point.group ?? '';
    const list = byGroup.get(key) ?? [];
    list.push(point);
    byGroup.set(key, list);
  }

  const series: Series[] = kept.map((group, index) => ({
    key: group.key,
    label: group.label,
    slot: index + 1,
    points: (byGroup.get(group.key) ?? []).map((p) => ({
      at: p.bucket,
      value: measureOf(p, measure),
    })),
  }));

  if (folded > 0) {
    const other = new Map<string, number | null>();
    for (const point of data.points) {
      if (keptKeys.has(point.group ?? '')) continue;
      const value = measureOf(point, measure);
      const sum = other.get(point.bucket) ?? null;
      other.set(point.bucket, value === null ? sum : (sum ?? 0) + value);
    }
    series.push({
      key: '__other__',
      label: `Other (${formatNumber(folded)})`,
      slot: 8,
      points: [...other.entries()].map(([at, value]) => ({ at, value })),
    });
  }
  return series;
}

// ── per-group totals ─────────────────────────────────────────────────────────

/** One group's totals over the window, summed from its points. */
interface GroupSummary extends UploadTotals {
  key: string;
  label: string;
  /** Source groups only. */
  origin: string | null;
  remote_aet: string | null;
  /** Share of the window's new studies, 0..1. */
  share: number | null;
}

const COUNT_FIELDS = [
  'studies_new',
  'series_new',
  'instances',
  'frames',
  'bytes',
  'legacy_instances',
] as const;

/** Splits a source key `origin|remote_aet` on its FIRST `|`. */
function splitSource(key: string): { origin: string; remote_aet: string | null } {
  const at = key.indexOf('|');
  if (at < 0) return { origin: key, remote_aet: null };
  return { origin: key.slice(0, at), remote_aet: key.slice(at + 1) || null };
}

function groupLabelFor(
  key: string,
  groupBy: UploadGroupBy,
  nameFor: (slug: string) => string,
  origin: string | null,
  remoteAet: string | null,
): string {
  if (groupBy === 'center') return nameFor(key) || key || UNKNOWN;
  if (groupBy === 'modality') return key || 'No modality tag';
  if (groupBy === 'source') {
    const from = origin || UNKNOWN;
    return remoteAet ? `${remoteAet} (${from})` : from;
  }
  return key || UNKNOWN;
}

/**
 * The response's group keys → per-group totals, summed from the points.
 *
 * A count is the sum of the non-null values; null when every point for the
 * group is null — "not computed" never becomes 0 by being summed.
 */
function summariseGroups(
  data: UploadSeriesResponse | null,
  nameFor: (slug: string) => string,
): GroupSummary[] {
  if (!data || data.group_by === 'none') return [];
  const byGroup = new Map<string, UploadPoint[]>();
  for (const point of data.points ?? []) {
    const key = point.group ?? '';
    const list = byGroup.get(key) ?? [];
    list.push(point);
    byGroup.set(key, list);
  }
  const total = data.totals?.studies_new ?? null;
  return (data.groups ?? []).map((key) => {
    const points = byGroup.get(key) ?? [];
    const sums = {} as Record<(typeof COUNT_FIELDS)[number], number | null>;
    for (const field of COUNT_FIELDS) {
      let sum: number | null = null;
      for (const p of points) {
        const value = p[field];
        if (value !== null && value !== undefined) sum = (sum ?? 0) + value;
      }
      sums[field] = sum;
    }
    let daysComputed = 0;
    let daysExpected = 0;
    for (const p of points) {
      daysComputed += p.days_computed ?? 0;
      daysExpected += p.days_expected ?? 0;
    }
    let origin: string | null = null;
    let remoteAet: string | null = null;
    if (data.group_by === 'source') {
      const first = points.find((p) => p.origin !== null || p.remote_aet !== null);
      const split = splitSource(key);
      origin = first?.origin ?? split.origin;
      remoteAet = first ? first.remote_aet : split.remote_aet;
    }
    return {
      key,
      label: groupLabelFor(key, data.group_by, nameFor, origin, remoteAet),
      origin,
      remote_aet: remoteAet,
      ...sums,
      days_computed: daysComputed,
      days_expected: daysExpected,
      share:
        sums.studies_new === null || total === null || total === 0
          ? null
          : sums.studies_new / total,
    };
  });
}

// ── table sorting (module-level accessors: useSortedRows wants them stable) ──

interface CenterRow {
  slug: string;
  name: string;
  studies: number | null;
  frames: number | null;
  instances: number | null;
  bytes: number | null;
  share: number | null;
  lastUpload: string | null;
  idle: IdleCenterRow | null;
  pending: PendingCenter | null;
}

type CenterSortKey = 'name' | 'studies' | 'frames' | 'instances' | 'bytes' | 'share' | 'lastUpload';

function centerAccessor(row: CenterRow, key: CenterSortKey): unknown {
  return row[key];
}

// ── the page ─────────────────────────────────────────────────────────────────

export function UploadsReportPage() {
  const [params, setParams] = useSearchParams();

  // One URL write per action, carrying every key it moves: a preset change
  // and its bucket reset land together, so there is one navigation and one
  // round of requests, never a request for the old bucket over the new window.
  const navigateTo = useCallback(
    (changes: Partial<Record<UrlKey, string | null>>, replace = false) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );

  const presetKey = readPreset(params.get('range'));
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[2]!;
  const isCustom = presetKey === 'custom';
  const slug = (params.get('slug') ?? '').trim();
  /** Scopes the edge section only: a node has no center-index counts of its
   *  own — its studies are counted in its center's index above. */
  const edgeNode = (params.get('node') ?? '').trim();
  const groupBy = readGroupBy(params.get('group_by'));
  const measure = readMeasure(params.get('measure'));
  const measureInfo = MEASURES.find((m) => m.key === measure) ?? MEASURES[0]!;

  // "Today" is Baghdad's today, whatever zone this browser is in — the same
  // day the server clamps `to` to.
  const today = baghdadToday();

  const window_ = useMemo(() => {
    if (!isCustom) {
      const range = presetRange(presetKey as RangePreset, today);
      return { ...range, invalid: '' };
    }
    const fallback = presetRange('30d', today);
    const from = params.get('from') ?? fallback.from;
    const to = params.get('to') ?? fallback.to;
    let invalid = '';
    if (!isDay(from) || !isDay(to)) invalid = 'Both dates must be real calendar days.';
    else if (from > to) invalid = 'The start date must not be after the end date.';
    else if (from > today) invalid = 'That range starts in the future; nothing is recorded there yet.';
    else if (daysInclusive(from, to) > MAX_WINDOW_DAYS)
      invalid = `A window can be at most ${MAX_WINDOW_DAYS} days. Choose a shorter range.`;
    return { from, to, invalid };
  }, [isCustom, presetKey, params, today]);

  const { from, to, invalid } = window_;
  // An explicit bucket survives only until the window changes (the preset
  // handler deletes it): 12 months by day is 365 slivers nobody asked for.
  const bucket =
    readBucket(params.get('bucket')) ??
    (isCustom && !invalid ? bucketForSpan(daysInclusive(from, to)) : preset.bucket);

  // ── requests ───────────────────────────────────────────────────────────────

  const query = useCallback(
    (extra: Record<string, string>) => {
      const qs = new URLSearchParams({ from, to, ...extra });
      if (slug) qs.set('slug', slug);
      return qs.toString();
    },
    [from, to, slug],
  );
  const seriesPath = (group: UploadGroupBy, groupBucket: UploadBucket = bucket) =>
    `/api/reports/uploads?${query({ bucket: groupBucket, group_by: group })}`;
  const ready = !invalid;

  // The chart's grouping, plus the three breakdowns the tables need. The
  // tables ask by month: their totals are summed from the points, and month
  // buckets keep a long window far below the point cap, so the sums are never
  // cut short. When the chart is grouped the same way as a table, that table
  // reuses the chart's response — unless the chart was truncated, whose
  // points (and so whose sums) are missing the oldest part of the window.
  const chart = useResource<UploadSeriesResponse>(seriesPath(groupBy), { enabled: ready });
  const chartTruncated = chart.data?.truncated === true;
  const reuseChart = (group: UploadGroupBy) => groupBy === group && !chartTruncated;
  const byCenterOwn = useResource<UploadSeriesResponse>(seriesPath('center', 'month'), {
    enabled: ready && !slug && !reuseChart('center'),
  });
  const byModalityOwn = useResource<UploadSeriesResponse>(seriesPath('modality', 'month'), {
    enabled: ready && !reuseChart('modality'),
  });
  const bySourceOwn = useResource<UploadSeriesResponse>(seriesPath('source', 'month'), {
    enabled: ready && !reuseChart('source'),
  });
  const byCenter = reuseChart('center') ? chart : byCenterOwn;
  const byModality = reuseChart('modality') ? chart : byModalityOwn;
  const bySource = reuseChart('source') ? chart : bySourceOwn;

  // The heatmap sends every count for every cell; the measure is picked here.
  const heatmapMeasure: UploadHeatmapMeasure = measure === 'bytes' ? 'studies' : measure;
  const heatmapField: keyof Pick<UploadHeatmapCell, 'studies_new' | 'instances' | 'frames'> =
    heatmapMeasure === 'studies' ? 'studies_new' : heatmapMeasure;
  const heatmap = useResource<UploadHeatmapResponse>(
    `/api/reports/uploads/heatmap?${query({})}`,
    { enabled: ready },
  );
  const storage = useResource<UploadStorageResponse>(
    `/api/reports/uploads/storage?${query({ bucket })}`,
    { enabled: ready },
  );
  const idle = useResource<IdleCentersResponse>(
    `/api/reports/uploads/idle?idle_days=${IDLE_THRESHOLD_DAYS}`,
  );
  const status = useResource<RollupStatusResponse>('/api/reports/uploads/status');
  const centerList = useResource<CenterListResponse>('/api/centers');
  // The Refresh button is shown to operators only. The server enforces the
  // role regardless; this just keeps a viewer from a button that can only 403.
  const me = useResource<MeResponse>('/api/me');
  const canRefresh = me.data?.role === 'operator' || me.data?.role === 'owner';
  // The server answers 503 while the rollup is disabled, so the button is
  // only live when it can work.
  const rollupEnabled = status.data?.enabled === true;

  // ── names ──────────────────────────────────────────────────────────────────

  const centerNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const center of centerList.data?.centers ?? []) {
      if (center.name) names.set(center.slug, center.name);
    }
    for (const row of idle.data?.centers ?? []) {
      if (row.name && !names.has(row.slug)) names.set(row.slug, row.name);
    }
    return names;
  }, [centerList.data, idle.data]);
  const nameFor = useCallback((key: string) => centerNames.get(key) || key, [centerNames]);

  const scopeLabel = slug ? nameFor(slug) : 'the whole fleet';
  const dateText = rangeText(from, to);
  const fileStem = (what: string) => `uploads-${slug || 'fleet'}-${what}-${from}-to-${to}`;
  const labelBucket = bucketLabeller(bucket);
  const format = formatMeasure(measure);

  /** A link to the same view, drilled into one center. */
  const drillTo = (target: string) => {
    const next = new URLSearchParams(params);
    next.set('slug', target);
    return `?${next.toString()}`;
  };

  // ── derived ────────────────────────────────────────────────────────────────

  const totals = chart.data?.totals ?? null;
  const coverage = chart.data?.coverage ?? null;
  const partialBuckets = useMemo(
    () => new Set((chart.data?.points ?? []).filter((p) => p.partial).map((p) => p.bucket)),
    [chart.data],
  );
  const incompleteBuckets = useMemo(
    () =>
      new Set((chart.data?.points ?? []).filter((p) => !isComplete(p)).map((p) => p.bucket)),
    [chart.data],
  );

  const chartGroups = useMemo(
    () => summariseGroups(chart.data, nameFor),
    [chart.data, nameFor],
  );
  const centerGroups = useMemo(
    () => summariseGroups(byCenter.data, nameFor),
    [byCenter.data, nameFor],
  );
  const modalityGroups = useMemo(
    () => summariseGroups(byModality.data, nameFor),
    [byModality.data, nameFor],
  );
  const sourceGroups = useMemo(
    () => summariseGroups(bySource.data, nameFor),
    [bySource.data, nameFor],
  );

  const series = useMemo(
    () => buildSeries(chart.data, chartGroups, measure, measureInfo.label),
    [chart.data, chartGroups, measure, measureInfo.label],
  );

  /**
   * Centers the rollup has not fully counted, from /status: failing, still
   * backfilling, or stale. While the rollup is disabled every center is
   * pending, and the reason is the rollup's own.
   */
  const pending = useMemo<PendingCenter[]>(() => {
    const st = status.data;
    if (!st) return [];
    return (st.centers ?? [])
      .filter((c) => !slug || c.slug === slug)
      .map((c) => {
        // A null earliest_day with a non-null history_from means the rollup
        // read the center's index and found nothing older to backfill (a
        // freshly created center): it is fully counted, not backfilling.
        const counted = c.backfill_complete || (c.history_from !== null && c.earliest_day === null);
        return { c, counted };
      })
      .filter(({ c, counted }) => c.last_error_kind !== null || !counted || c.stale)
      .map(({ c, counted }) => ({
        slug: c.slug,
        status: c.last_error_kind ?? (counted ? 'stale' : c.history_from ? 'backfilling' : 'pending'),
        reason: c.last_error ?? (st.enabled ? '' : st.reason),
        history_from: c.history_from,
      }));
  }, [status.data, slug]);

  /** The stalest center in scope: the view is only as fresh as it. */
  const oldestRefresh = useMemo(() => {
    let oldest: string | null = null;
    for (const c of status.data?.centers ?? []) {
      if (slug && c.slug !== slug) continue;
      if (!c.last_success_at) continue;
      if (oldest === null || Date.parse(c.last_success_at) < Date.parse(oldest))
        oldest = c.last_success_at;
    }
    return oldest;
  }, [status.data, slug]);

  const idleRows = useMemo(() => {
    const rows = idle.data?.centers ?? [];
    return slug ? rows.filter((r) => r.slug === slug) : rows;
  }, [idle.data, slug]);
  const idleCounts = useMemo(() => {
    // `never` is an idle center with no upload on record (the server calls it idle).
    const counts = { active: 0, idle: 0, never: 0, unknown: 0 };
    for (const row of idleRows) {
      if (row.status === 'idle' && row.last_upload_at === null) counts.never += 1;
      else if (row.status === 'idle') counts.idle += 1;
      else if (row.status === 'active') counts.active += 1;
      else counts.unknown += 1;
    }
    return counts;
  }, [idleRows]);
  const idleThreshold = idle.data?.idle_days ?? IDLE_THRESHOLD_DAYS;

  const centerRows = useMemo<CenterRow[]>(() => {
    const idleBySlug = new Map((idle.data?.centers ?? []).map((r) => [r.slug, r]));
    const pendingBySlug = new Map(pending.map((p) => [p.slug, p]));
    return centerGroups.map((group) => {
      const idleRow = idleBySlug.get(group.key) ?? null;
      return {
        slug: group.key,
        name: group.label,
        studies: group.studies_new,
        frames: group.frames,
        instances: group.instances,
        bytes: group.bytes,
        share: group.share,
        lastUpload: idleRow?.last_upload_at ?? null,
        idle: idleRow,
        pending: pendingBySlug.get(group.key) ?? null,
      };
    });
  }, [centerGroups, idle.data, pending]);
  const centerSort = useSort<CenterSortKey>('studies', 'desc');
  const sortedCenters = useSortedRows(centerRows, centerSort, centerAccessor);

  // Cells are totals over the window; weekday 0 = Monday. Dividing by the
  // computed center-days of that weekday gives a per-center-day average.
  const heatValues = useMemo(() => {
    if (!heatmap.data) return null;
    const days = heatmap.data.days_by_weekday ?? [];
    const grid: (number | null)[][] = WEEKDAYS.map(() => Array<number | null>(24).fill(null));
    for (const cell of heatmap.data.cells ?? []) {
      const row = grid[cell.weekday];
      if (!row || cell.hour < 0 || cell.hour >= 24) continue;
      const value = cell[heatmapField];
      const dayCount = days[cell.weekday] ?? 0;
      row[cell.hour] = value === null || value === undefined || dayCount === 0 ? null : value / dayCount;
    }
    return grid;
  }, [heatmap.data, heatmapField]);
  const busiest = useMemo(() => {
    let best: { weekday: number; hour: number; total: number } | null = null;
    for (const cell of heatmap.data?.cells ?? []) {
      const value = cell[heatmapField];
      if (value === null || value === undefined) continue;
      if (best === null || value > best.total)
        best = { weekday: cell.weekday, hour: cell.hour, total: value };
    }
    return best;
  }, [heatmap.data, heatmapField]);

  const receivedSeries = useMemo<Series[]>(
    () =>
      storage.data
        ? [
            {
              key: 'received',
              label: 'Received',
              slot: 1,
              points: (storage.data.points ?? []).map((p) => ({
                at: p.bucket,
                value: p.received_bytes,
              })),
            },
          ]
        : [],
    [storage.data],
  );
  const storedSeries = useMemo<Series[]>(
    () =>
      storage.data
        ? [
            {
              key: 'stored',
              label: 'Stored',
              slot: 2,
              points: (storage.data.points ?? []).map((p) => ({
                at: dayAsInstant(p.bucket),
                value: p.stored_bytes,
              })),
            },
          ]
        : [],
    [storage.data],
  );
  const storagePartial = useMemo(
    () => new Set((storage.data?.points ?? []).filter((p) => p.partial).map((p) => p.bucket)),
    [storage.data],
  );

  // ── refresh ────────────────────────────────────────────────────────────────

  const [refresh, setRefresh] = useState<{ busy: boolean; message: ReactNode; tone: Tone } | null>(
    null,
  );
  const onRefresh = async () => {
    setRefresh({ busy: true, message: null, tone: 'info' });
    try {
      const answer = await apiPost<RollupRefreshResponse>('/api/reports/uploads/refresh');
      const detail = answer?.detail ? `${answer.detail[0]?.toUpperCase() ?? ''}${answer.detail.slice(1)}.` : '';
      setRefresh({
        busy: false,
        tone: 'good',
        message: `${detail ? `${detail} ` : ''}The rollup works in the background and can take a few minutes; the numbers here change when it finishes — reload this page then.`,
      });
      status.reload();
    } catch (error) {
      setRefresh({ busy: false, tone: 'critical', message: describeError(error) });
    }
  };

  // ── CSV ────────────────────────────────────────────────────────────────────

  const exportSeries = () => {
    const data = chart.data;
    if (!data) return;
    const labels = new Map(chartGroups.map((g) => [g.key, g.label]));
    const rows: CsvCell[][] = (data.points ?? []).map((p) => [
      p.bucket,
      p.bucket_end,
      ...(data.group_by === 'none' ? [] : [p.group, labels.get(p.group ?? '') ?? p.group]),
      p.studies_new,
      p.frames,
      p.instances,
      p.bytes,
      p.legacy_instances,
      p.days_computed,
      p.days_expected,
      p.partial,
      isComplete(p),
    ]);
    downloadCsv(
      `${fileStem(data.group_by === 'none' ? 'total' : data.group_by)}.csv`,
      [
        'bucket',
        'bucket_end',
        ...(data.group_by === 'none' ? [] : ['group', 'group_label']),
        'studies',
        'images_frames',
        'instances',
        'bytes',
        'legacy_instances',
        'days_computed',
        'days_expected',
        'partial',
        'complete',
      ],
      rows,
    );
  };

  const exportGroups = (
    data: UploadSeriesResponse | null,
    groups: GroupSummary[],
    what: string,
  ) => {
    if (!data) return;
    const source = data.group_by === 'source';
    downloadCsv(
      `${fileStem(what)}.csv`,
      [
        'key',
        'label',
        ...(source ? ['origin', 'remote_aet'] : []),
        'studies',
        'images_frames',
        'instances',
        'bytes',
        'share_of_studies',
        'days_computed',
        'days_expected',
      ],
      groups.map((g) => [
        g.key,
        g.label,
        ...(source ? [g.origin, g.remote_aet] : []),
        g.studies_new,
        g.frames,
        g.instances,
        g.bytes,
        g.share,
        g.days_computed,
        g.days_expected,
      ]),
    );
  };

  const exportCenters = () => {
    downloadCsv(
      `${fileStem('centers')}.csv`,
      [
        'slug',
        'name',
        'studies',
        'images_frames',
        'instances',
        'bytes',
        'share_of_studies',
        'last_upload_at',
        'idle_status',
        'rollup_status',
      ],
      sortedCenters.map((r) => [
        r.slug,
        r.name,
        r.studies,
        r.frames,
        r.instances,
        r.bytes,
        r.share,
        r.lastUpload,
        r.idle?.status ?? null,
        r.pending?.status ?? null,
      ]),
    );
  };

  const exportStorage = () => {
    const data = storage.data;
    if (!data) return;
    downloadCsv(
      `${fileStem('storage')}.csv`,
      [
        'bucket',
        'bucket_end',
        'received_bytes',
        'received_days_computed',
        'stored_bytes',
        'stored_studies',
        'stored_instances',
        'stored_as_of',
        'centers_reporting',
        'partial',
      ],
      (data.points ?? []).map((p) => [
        p.bucket,
        p.bucket_end,
        p.received_bytes,
        p.received_days_computed,
        p.stored_bytes,
        p.stored_studies,
        p.stored_instances,
        p.stored_as_of,
        p.centers_reporting,
        p.partial,
      ]),
    );
  };

  const exportHeatmap = () => {
    const data = heatmap.data;
    if (!data) return;
    const days = data.days_by_weekday ?? [];
    downloadCsv(
      `${fileStem(`heatmap-${heatmapMeasure}`)}.csv`,
      ['iso_weekday', 'weekday', 'hour_baghdad', 'total', 'per_day_avg', 'days_counted'],
      [...(data.cells ?? [])]
        .sort((a, b) => a.weekday - b.weekday || a.hour - b.hour)
        .map((c) => {
          const total = c[heatmapField] ?? null;
          const dayCount = days[c.weekday] ?? null;
          return [
            c.weekday + 1,
            WEEKDAYS[c.weekday] ?? null,
            c.hour,
            total,
            total === null || !dayCount ? null : total / dayCount,
            dayCount,
          ];
        }),
    );
  };

  const exportIdle = () => {
    downloadCsv(
      `uploads-${slug || 'fleet'}-idle-${idle.data?.checked_at?.slice(0, 10) ?? today}.csv`,
      [
        'slug',
        'name',
        'status',
        'reason',
        'last_upload_at',
        'days_since_upload',
        'last_success_at',
      ],
      idleRows.map((r) => [
        r.slug,
        r.name ?? nameFor(r.slug),
        r.status === 'idle' && r.last_upload_at === null ? 'never' : r.status,
        r.reason,
        r.last_upload_at,
        r.days_since_upload,
        r.last_success_at,
      ]),
    );
  };

  const csvButton = (onClick: () => void, what: string, disabled = false) => (
    <Button kind="ghost" size="sm" renderIcon={DownloadIcon} onClick={onClick} disabled={disabled}>
      CSV<span className="visually-hidden"> of {what}</span>
    </Button>
  );

  // ── render helpers ─────────────────────────────────────────────────────────

  /** A breakdown as ranked bars, biggest first, by the chosen measure. */
  const rankedRows = (groups: GroupSummary[]) =>
    groups
      .map((g) => ({
        key: g.key,
        label: g.label as ReactNode,
        value: measureOf(g, measure),
      }))
      .sort((a, b) => (b.value ?? -1) - (a.value ?? -1));

  const groupsTable = (groups: GroupSummary[], label: string, keyHeading: string) => (
    <TableScroll label={label}>
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{keyHeading}</th>
            <th scope="col" className="numeric">Studies</th>
            <th scope="col" className="numeric">Images</th>
            <th scope="col" className="numeric">Instances</th>
            <th scope="col" className="numeric">Data</th>
            <th scope="col" className="numeric">Share</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.key}>
              <td>{g.label}</td>
              <td className="numeric">{formatNumber(g.studies_new)}</td>
              <td className="numeric">{formatNumber(g.frames)}</td>
              <td className="numeric">{formatNumber(g.instances)}</td>
              <td className="numeric">{formatBytes(g.bytes)}</td>
              <td className="numeric">{sharePercent(g.share)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );

  const loadingOr = (
    resource: { loading: boolean; error: unknown; reload: () => void },
    what: string,
    body: () => ReactNode,
  ): ReactNode => {
    if (!ready) return <p className="viz__empty">Choose a valid window.</p>;
    if (resource.loading) return <LoadingBlock label={`Loading ${what}…`} />;
    if (resource.error)
      return <ErrorBlock what={what} error={resource.error} onRetry={resource.reload} />;
    return body();
  };

  const lastTick = status.data?.last_run_finished_at ?? status.data?.last_run_started_at ?? null;

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="What arrived at each center, counted from its own index, by Baghdad day"
      />

      <div className="bento">
        <ReportTabs className="bento__full" />

        {/* ── filters ────────────────────────────────────────────────────── */}
        <div className="bento__full toolbar">
          <Select
            id="uploads-range"
            labelText="Window"
            value={presetKey}
            onChange={(event) => {
              const next = readPreset(event.target.value);
              // A new window resets the bucket to the one that suits it, and
              // switching to Custom starts from the window already on screen
              // so the chart does not jump to some other range.
              navigateTo({
                range: next === DEFAULT_PRESET ? null : next,
                bucket: null,
                from: next === 'custom' ? from : null,
                to: next === 'custom' ? to : null,
              });
            }}
          >
            {PRESETS.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          {isCustom ? (
            <>
              <TextInput
                id="uploads-from"
                labelText="From (Baghdad day)"
                type="date"
                value={from}
                max={today}
                onChange={(event) => navigateTo({ from: event.target.value, bucket: null }, true)}
              />
              <TextInput
                id="uploads-to"
                labelText="To (Baghdad day)"
                type="date"
                value={to}
                max={today}
                onChange={(event) => navigateTo({ to: event.target.value, bucket: null }, true)}
              />
            </>
          ) : null}

          <Select
            id="uploads-bucket"
            labelText="Bucket"
            value={bucket}
            onChange={(event) => navigateTo({ bucket: event.target.value })}
          >
            {BUCKETS.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          <Select
            id="uploads-scope"
            labelText="Scope"
            value={slug}
            onChange={(event) => navigateTo({ slug: event.target.value || null })}
          >
            <SelectItem value="" text="Whole fleet" />
            {slug && !(centerList.data?.centers ?? []).some((c) => c.slug === slug) ? (
              <SelectItem value={slug} text={`Center — ${nameFor(slug)}`} />
            ) : null}
            {(centerList.data?.centers ?? []).map((center) => (
              <SelectItem
                key={center.slug}
                value={center.slug}
                text={`Center — ${center.name || center.slug}`}
              />
            ))}
          </Select>

          <Select
            id="uploads-group"
            labelText="Split chart by"
            value={groupBy}
            onChange={(event) =>
              navigateTo({ group_by: event.target.value === 'none' ? null : event.target.value })
            }
          >
            {GROUPINGS.filter((g) => !(slug && g.key === 'center')).map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          <Select
            id="uploads-measure"
            labelText="Measure"
            value={measure}
            onChange={(event) =>
              navigateTo({ measure: event.target.value === 'studies' ? null : event.target.value })
            }
          >
            {MEASURES.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          {canRefresh ? (
            <Button
              kind="tertiary"
              size="sm"
              className="push"
              renderIcon={RenewIcon}
              disabled={refresh?.busy === true || !rollupEnabled}
              title={rollupEnabled ? undefined : status.data?.reason || 'The upload rollup is not running.'}
              onClick={() => void onRefresh()}
            >
              {refresh?.busy ? 'Asking…' : 'Refresh now'}
            </Button>
          ) : null}
        </div>

        {slug ? (
          <p className="bento__full">
            Showing <strong>{nameFor(slug)}</strong>
            {centerNames.has(slug) ? <span className="muted"> · {slug}</span> : null}.{' '}
            <Link to={`?${(() => {
              const next = new URLSearchParams(params);
              next.delete('slug');
              return next.toString();
            })()}`}
            >
              Back to the whole fleet
            </Link>
          </p>
        ) : null}

        {invalid ? (
          <Callout className="bento__full" tone="warning" title="That window cannot be shown">
            {invalid}
          </Callout>
        ) : null}

        {refresh && refresh.message ? (
          <Callout
            className="bento__full"
            tone={refresh.tone}
            title={refresh.tone === 'critical' ? 'The refresh was not queued' : 'Refresh queued'}
            live
          >
            {refresh.message}
          </Callout>
        ) : null}

        {chart.data?.unavailable_reason ? (
          <Callout className="bento__full" tone="warning" title="Upload counts are not available">
            {chart.data.unavailable_reason}
          </Callout>
        ) : status.data && !status.data.enabled && status.data.reason ? (
          <Callout className="bento__full" tone="warning" title="Upload counts are not configured">
            {status.data.reason}
          </Callout>
        ) : null}

        {status.data && (status.data.unavailable_reason || status.data.last_run_error) ? (
          <Callout className="bento__full" tone="warning" title="The upload rollup reported a problem">
            {status.data.unavailable_reason ? <p>{status.data.unavailable_reason}</p> : null}
            {status.data.last_run_error ? (
              <p>Last run: {status.data.last_run_error}</p>
            ) : null}
          </Callout>
        ) : null}

        {pending.length > 0 ? (
          <Callout
            className="bento__full"
            tone="info"
            title={`${formatNumber(pending.length)} ${pluralise(
              pending.length,
              'center is',
              'centers are',
            )} not fully counted yet`}
          >
            <p>
              The numbers on this page leave these out where they have no counts. A day
              before a center&rsquo;s history starts is <strong>not computed</strong>, not a
              day with nothing in it.
            </p>
            <ul>
              {pending.map((p) => (
                <li key={p.slug}>
                  <Badge tone={PENDING_TONE[p.status] ?? 'neutral'} small>
                    {p.status}
                  </Badge>{' '}
                  <Link to={drillTo(p.slug)}>{nameFor(p.slug)}</Link>
                  {p.history_from ? <> — counted from {p.history_from}</> : null}
                  {p.reason ? <span className="muted"> · {p.reason}</span> : null}
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}

        {chart.data?.truncated ? (
          <Callout className="bento__full" tone="warning" title="Series truncated">
            This window holds more buckets than one response carries, so only the most
            recent are shown
            {chart.data.points[0] ? <> — from {chart.data.points[0].bucket}</> : null}. The
            oldest part of the window is missing, not empty. Choose a coarser bucket, a
            shorter window or fewer groups to see all of it.
          </Callout>
        ) : null}

        {incompleteBuckets.size > 0 && pending.length === 0 ? (
          <Callout className="bento__full" tone="info" title="Some buckets are incomplete">
            {formatNumber(incompleteBuckets.size)} {pluralise(incompleteBuckets.size, 'bucket')}{' '}
            in this window {incompleteBuckets.size === 1 ? 'is' : 'are'} summed over only
            some centers — not every center had been counted for every day of it. The
            exported CSV marks them <code>complete=false</code>.
          </Callout>
        ) : null}

        {chart.error && !chart.data ? (
          <div className="bento__full">
            <ErrorBlock what="the upload counts" error={chart.error} onRetry={chart.reload} />
          </div>
        ) : null}

        {/* ── headline ───────────────────────────────────────────────────── */}
        <div className="bento__full">
          <StatGrid>
            <StatTile
              label="Studies"
              value={formatNumber(totals?.studies_new)}
              hint={`new, ${scopeLabel}, ${dateText}`}
            />
            <StatTile
              label="Images (frames)"
              value={formatNumber(totals?.frames)}
              hint={
                totals?.legacy_instances
                  ? `${formatNumber(totals.legacy_instances)} legacy ${pluralise(
                      totals.legacy_instances,
                      'instance',
                    )} assumed to be images`
                  : 'frames of image instances, same window'
              }
            />
            <StatTile
              label="Instances"
              value={formatNumber(totals?.instances)}
              hint="every DICOM object received, same window"
            />
            <StatTile
              label="Data"
              value={formatBytes(totals?.bytes)}
              hint="received, same window"
            />
            <StatTile
              label="Idle centers"
              value={idle.data ? formatNumber(idleCounts.idle) : UNKNOWN}
              tone={idleCounts.idle > 0 ? 'warning' : undefined}
              hint={
                idle.data
                  ? `no upload for ${idleThreshold}+ days · ${formatNumber(
                      idleCounts.never,
                    )} never · ${formatNumber(idleCounts.unknown)} unknown`
                  : `no upload for ${IDLE_THRESHOLD_DAYS}+ days`
              }
            />
          </StatGrid>
        </div>

        {/* ── the main chart ─────────────────────────────────────────────── */}
        <Card className="bento__full">
          <div className="card__body">
            <ChartFrame
              title={`${measureInfo.label} per ${bucket === 'day' ? 'day' : bucket === 'week' ? 'week' : 'month'}`}
              subtitle={
                <>
                  {scopeLabel === 'the whole fleet' ? 'Whole fleet' : scopeLabel}, {dateText}.
                  {groupBy !== 'none' ? ' Stacked: the parts add up to the total.' : ''}{' '}
                  A faded column is still counting; an empty slot was not computed, a flat
                  tick is a counted zero.
                </>
              }
              series={series}
              unavailable={chart.data?.unavailable_reason || undefined}
              coverage={
                totals && coverage ? (
                  <>
                    {formatNumber(totals.days_computed)} of {formatNumber(totals.days_expected)}{' '}
                    center-days counted
                    {coverage.earliest ? (
                      <>
                        ; computed from {coverage.earliest} to {coverage.latest ?? UNKNOWN}
                      </>
                    ) : null}
                    {oldestRefresh ? <>; oldest refresh {formatRelative(oldestRefresh)}</> : null}
                    {lastTick ? <>; last rollup run {formatDateTime(lastTick)}</> : null}
                    .{coverage.reason ? <> {coverage.reason}</> : null}
                  </>
                ) : undefined
              }
              table={
                chart.data ? (
                  <ColumnChartTable
                    series={series}
                    partial={partialBuckets}
                    format={format}
                    bucketLabel={labelBucket}
                    label={`${measureInfo.label} per bucket`}
                  />
                ) : undefined
              }
            >
              {loadingOr(chart, 'the upload counts', () => (
                <ColumnChart
                  series={series}
                  mode="stacked"
                  partial={partialBuckets}
                  format={format}
                  bucketLabel={labelBucket}
                  label={`${measureInfo.label} per ${bucket}, ${dateText}`}
                />
              ))}
            </ChartFrame>
            <div className="row">
              <span className="push">{csvButton(exportSeries, 'the chart', !chart.data)}</span>
            </div>
          </div>
        </Card>

        {/* ── the hospital side of the same flow ─────────────────────────── */}
        {/* Its own cards and its own totals, scoped by the same slug (or a
            `?node=` from a node's page): edge counts are never added to the
            numbers above, because replicated studies are already in them. */}
        <EdgeUploadsSection
          fromDay={from}
          toDay={to}
          bucket={bucket}
          slug={slug || null}
          node={edgeNode || null}
          scopeLabel={edgeNode ? 'this node' : scopeLabel}
          ready={ready}
        />

        {/* ── per center ─────────────────────────────────────────────────── */}
        {slug ? null : (
          <Card className="bento__full">
            <div className="card__body">
              <ChartFrame
                title="By center"
                subtitle={`${dateText}. Pick a center to see this page for it alone.`}
              >
                {loadingOr(byCenter, 'the per-center counts', () =>
                  centerRows.length === 0 ? (
                    <p className="viz__empty">No center has counts in this window yet.</p>
                  ) : (
                    <TableScroll label="Uploads by center">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <SortHeader columnKey="name" sort={centerSort}>Center</SortHeader>
                            <SortHeader columnKey="studies" sort={centerSort} numeric>Studies</SortHeader>
                            <SortHeader columnKey="frames" sort={centerSort} numeric>Images</SortHeader>
                            <SortHeader columnKey="instances" sort={centerSort} numeric>Instances</SortHeader>
                            <SortHeader columnKey="bytes" sort={centerSort} numeric>Data</SortHeader>
                            <SortHeader columnKey="share" sort={centerSort} numeric>Share</SortHeader>
                            <SortHeader columnKey="lastUpload" sort={centerSort}>Last upload</SortHeader>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCenters.map((row) => {
                            const badge = row.pending
                              ? { tone: PENDING_TONE[row.pending.status] ?? 'neutral', text: row.pending.status }
                              : row.idle
                                ? idleBadge(row.idle)
                                : null;
                            return (
                              <tr key={row.slug}>
                                <td>
                                  <Link to={drillTo(row.slug)}>{row.name}</Link>
                                  {row.name !== row.slug ? (
                                    <span className="muted"> · {row.slug}</span>
                                  ) : null}
                                </td>
                                <td className="numeric">{formatNumber(row.studies)}</td>
                                <td className="numeric">{formatNumber(row.frames)}</td>
                                <td className="numeric">{formatNumber(row.instances)}</td>
                                <td className="numeric">{formatBytes(row.bytes)}</td>
                                <td className="numeric">{sharePercent(row.share)}</td>
                                <td>
                                  {row.lastUpload ? (
                                    <time dateTime={row.lastUpload} title={formatDateTime(row.lastUpload)}>
                                      {formatRelative(row.lastUpload)}
                                    </time>
                                  ) : (
                                    UNKNOWN
                                  )}
                                </td>
                                <td>
                                  {badge ? (
                                    <Badge
                                      tone={badge.tone}
                                      small
                                      title={row.pending?.reason || row.idle?.reason || undefined}
                                    >
                                      {badge.text}
                                    </Badge>
                                  ) : (
                                    UNKNOWN
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </TableScroll>
                  ),
                )}
              </ChartFrame>
              <div className="row">
                <span className="push">
                  {csvButton(exportCenters, 'the per-center table', centerRows.length === 0)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* ── modality and sender ────────────────────────────────────────── */}
        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame
              title="By modality"
              subtitle={`${measureInfo.label}, ${dateText}. A study counts under the modality of the series that arrived first.`}
              table={groupsTable(modalityGroups, 'Uploads by modality', 'Modality')}
            >
              {loadingOr(byModality, 'the modality breakdown', () => (
                <RankedBars
                  rows={rankedRows(modalityGroups)}
                  format={format}
                  label={`${measureInfo.label} by modality`}
                />
              ))}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(
                  () => exportGroups(byModality.data, modalityGroups, 'modality'),
                  'the modality table',
                  !byModality.data,
                )}
              </span>
            </div>
          </div>
        </Card>

        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame
              title="By sender"
              subtitle={
                <>
                  {measureInfo.label}, {dateText}. Scanners sending over DICOM are named by AE
                  title. Anything behind a hospital edge node arrives as{' '}
                  <strong>REST API</strong> — its scanner is only known at the edge.
                </>
              }
              table={groupsTable(sourceGroups, 'Uploads by sender', 'Sender')}
            >
              {loadingOr(bySource, 'the sender breakdown', () => (
                <RankedBars
                  rows={rankedRows(sourceGroups)}
                  format={format}
                  label={`${measureInfo.label} by sender`}
                />
              ))}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(
                  () => exportGroups(bySource.data, sourceGroups, 'source'),
                  'the sender table',
                  !bySource.data,
                )}
              </span>
            </div>
          </div>
        </Card>

        {/* ── storage ────────────────────────────────────────────────────── */}
        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame
              title="Data received"
              subtitle={`Bytes that arrived per ${bucket}, ${dateText}. Gross: a study later deleted still counts here.`}
              unavailable={storage.data?.unavailable_reason || undefined}
              table={
                storage.data ? (
                  <ColumnChartTable
                    series={receivedSeries}
                    partial={storagePartial}
                    format={(v) => formatBytes(v)}
                    bucketLabel={labelBucket}
                    label="Data received per bucket"
                  />
                ) : undefined
              }
            >
              {loadingOr(storage, 'storage growth', () => (
                <ColumnChart
                  series={receivedSeries}
                  partial={storagePartial}
                  format={(v) => formatBytes(v)}
                  bucketLabel={labelBucket}
                  label={`Data received per ${bucket}`}
                />
              ))}
            </ChartFrame>
          </div>
        </Card>

        <Card className="bento__half">
          <div className="card__body">
            <ChartFrame
              title="Data stored"
              subtitle="Net: what the centers held at the end of each bucket, after deletions. A second chart, not a second axis — the two are different quantities."
              unavailable={storage.data?.unavailable_reason || undefined}
              coverage={storage.data?.coverage?.reason || undefined}
              table={
                storage.data ? (
                  <TableScroll label="Data stored per bucket">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">Bucket</th>
                          <th scope="col" className="numeric">Stored</th>
                          <th scope="col" className="numeric">Studies held</th>
                          <th scope="col" className="numeric">Instances held</th>
                          <th scope="col">As of</th>
                          <th scope="col" className="numeric">Centers reporting</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(storage.data.points ?? []).map((p) => (
                          <tr key={p.bucket}>
                            <td>
                              {labelBucket(p.bucket)}
                              {p.partial ? <span className="muted"> · partial</span> : null}
                            </td>
                            <td className="numeric">{formatBytes(p.stored_bytes)}</td>
                            <td className="numeric">{formatNumber(p.stored_studies)}</td>
                            <td className="numeric">{formatNumber(p.stored_instances)}</td>
                            <td>{p.stored_as_of ?? UNKNOWN}</td>
                            <td className="numeric">{formatNumber(p.centers_reporting)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ) : undefined
              }
            >
              {loadingOr(storage, 'storage growth', () => (
                <LineChart
                  series={storedSeries}
                  label="Data stored over time"
                  format={(v) => formatBytes(v)}
                />
              ))}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(exportStorage, 'the storage table', !storage.data)}
              </span>
            </div>
          </div>
        </Card>

        {/* ── when, and who has gone quiet ───────────────────────────────── */}
        <Card className="bento__two-thirds">
          <div className="card__body">
            <ChartFrame
              title="Busiest hours"
              subtitle={
                <>
                  Average{' '}
                  {heatmapMeasure === 'frames' ? 'images' : heatmapMeasure} per weekday and
                  hour, Baghdad time, {dateText}
                  {measure === 'bytes' ? ' (studies: data is not counted by the hour)' : ''} —
                  per center-day: each total is divided by the center-days of that weekday
                  that were counted.
                  {busiest ? (
                    <>
                      {' '}Busiest: {WEEKDAYS[busiest.weekday]}{' '}
                      {String(busiest.hour).padStart(2, '0')}:00, with{' '}
                      {formatNumber(busiest.total)} in all.
                    </>
                  ) : null}
                </>
              }
              unavailable={heatmap.data?.unavailable_reason || undefined}
              table={
                heatValues ? (
                  <HeatmapTable
                    values={heatValues}
                    format={formatAverage}
                    label="Average uploads per center-day, by weekday and hour"
                  />
                ) : undefined
              }
            >
              {loadingOr(heatmap, 'the hourly pattern', () =>
                heatValues ? (
                  <Heatmap
                    values={heatValues}
                    format={formatAverage}
                    label="Average uploads per center-day, by weekday and hour, Baghdad time"
                  />
                ) : null,
              )}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(exportHeatmap, 'the hourly pattern', !heatmap.data)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="bento__third">
          <div className="card__body">
            <ChartFrame
              title="Idle centers"
              subtitle={`No upload for ${idleThreshold} days or more, as of now — not tied to the window above. "Unknown" means the count itself is stale or failing, which is not the same as quiet.`}
              unavailable={idle.data?.unavailable_reason || undefined}
            >
              {idle.loading ? (
                <LoadingBlock label="Loading idle centers…" />
              ) : idle.error ? (
                <ErrorBlock what="idle centers" error={idle.error} onRetry={idle.reload} />
              ) : idleRows.filter((r) => r.status !== 'active').length === 0 ? (
                <p className="viz__empty">
                  {idleRows.length === 0
                    ? 'No centers to report on.'
                    : 'Every center has uploaded recently.'}
                </p>
              ) : (
                <ul className="stack" aria-label="Centers that are idle, never uploaded, or unknown">
                  {idleRows
                    .filter((r) => r.status !== 'active')
                    .map((row) => {
                      const badge = idleBadge(row);
                      return (
                        <li key={row.slug}>
                          <div className="row">
                            <Link to={drillTo(row.slug)}>{row.name || nameFor(row.slug)}</Link>
                            <span className="push">
                              <Badge tone={badge.tone} small title={row.reason || undefined}>
                                {badge.text}
                              </Badge>
                            </span>
                          </div>
                          <span className="muted">
                            {row.status === 'unknown'
                              ? row.reason || 'Not counted recently.'
                              : row.last_upload_at
                                ? `Last upload ${formatDateTime(row.last_upload_at)}`
                                : 'No upload on record.'}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              )}
              {idle.data ? (
                <p className="muted">
                  {formatNumber(idleCounts.active)} {pluralise(idleCounts.active, 'center')}{' '}
                  active.
                </p>
              ) : null}
            </ChartFrame>
            <div className="row">
              <span className="push">
                {csvButton(exportIdle, 'the idle-center list', idleRows.length === 0)}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}

/** Per-day averages are small fractional numbers; one decimal below 10. */
function formatAverage(value: number): string {
  return value < 10 ? value.toFixed(1) : formatNumber(Math.round(value));
}

